import { useMemo } from 'react';
import { S, guestbookSeed, t, tList, type Locale } from '../i18n/strings';
import { useSteve } from './context';
import type { LogEntry, SimpleLogKey, VariantLogKey } from './types';

/** Ligne de journal prête à afficher. */
export interface LogLine {
  readonly id: number;
  readonly text: string;
}

/** Entrée de livre d'or prête à afficher. */
export interface GuestbookLine {
  readonly id: number;
  readonly who: string;
  readonly text: string;
}

const SIMPLE: Record<SimpleLogKey, { fr: string; en: string }> = {
  song_start: S.log.song_start,
  song_stop: S.log.song_stop,
  pad_stop: S.log.pad_stop,
  hold_on: S.log.hold_on,
  hold_off: S.log.hold_off,
  wobble_on: S.log.wobble_on,
  wobble_off: S.log.wobble_off,
  preset_normal: S.log.preset_normal,
  preset_deep: S.log.preset_deep,
  preset_fast: S.log.preset_fast,
  preset_choir: S.log.preset_choir,
  guestbook: S.log.guestbook,
  sb_theme: S.log.sb_theme,
  kikoo: S.log.kikoo,
  kikoo_off: S.log.kikoo_off,
};

const VARIANTS: Record<VariantLogKey, { fr: readonly string[]; en: readonly string[] }> = {
  boot: S.log.boot,
  feed: S.log.feed,
  praise: S.log.praise,
  pad: S.log.pad,
};

/** Résout une ligne de journal dans la langue courante. */
export function resolveLogEntry(entry: LogEntry, lang: Locale): string {
  switch (entry.kind) {
    case 'simple':
      return t(SIMPLE[entry.key], lang);
    case 'variant': {
      const list = tList(VARIANTS[entry.key], lang);
      // Modulo : les listes FR et EN pourraient un jour ne plus avoir la même
      // longueur, une variante hors bornes ne doit pas produire de trou.
      return list.length === 0 ? '' : (list[entry.variant % list.length] ?? '');
    }
    case 'bubbles':
      return t(S.log.bubbles, lang, { n: entry.count });
  }
}

/** Le journal du bac, localisé, le plus récent en tête. */
export function useLogLines(): readonly LogLine[] {
  const { log, lang } = useSteve();
  return useMemo(
    () => log.map((entry) => ({ id: entry.id, text: resolveLogEntry(entry, lang) })),
    [log, lang],
  );
}

/**
 * La signature du visiteur : son pseudo kikoo s'il en a adopté un au générateur
 * du skyblog, `visiteur_anonyme` sinon.
 *
 * Elle vaut pour les onze interfaces — c'est là que le générateur sort de son
 * skin.
 */
export function useSignature(): string {
  const { kikoo, lang } = useSteve();
  return kikoo === '' ? t(S.guestbook.anonymous, lang) : kikoo;
}

/** Le livre d'or, localisé pour les trois entrées de départ. */
export function useGuestbookLines(): readonly GuestbookLine[] {
  const { guestbook, lang } = useSteve();
  const signature = useSignature();
  return useMemo(() => {
    const seeds = guestbookSeed(lang);
    return guestbook.flatMap<GuestbookLine>((entry) => {
      if (entry.kind === 'visitor') {
        return [{ id: entry.id, who: signature, text: entry.text }];
      }
      const seed = seeds[entry.index];
      return seed ? [{ id: entry.id, who: seed.who, text: seed.text }] : [];
    });
  }, [guestbook, lang, signature]);
}

/** Libellé du bouton de lecture et ligne d'état du son. */
export function useAudioText(): { readonly label: string; readonly hint: string } {
  const { playing, audioMissing, lang } = useSteve();
  return useMemo(() => {
    const label = t(playing ? S.common.song_pause : S.common.song_play, lang);
    const hint = audioMissing
      ? t(S.common.audio_missing, lang)
      : t(playing ? S.common.audio_playing : S.common.audio_idle, lang);
    return { label, hint };
  }, [playing, audioMissing, lang]);
}
